import { apiClient } from '@src/services/http-common'
import { Person, PersonCreateDto } from '@src/types/Person'
import { UseMutateAsyncFunction, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'

const PERSON_SCOPE = 'person'
const LIST = 'list'
const DETAIL = 'detail'

const keysFactory = {
  allPerson: () => [{ scope: PERSON_SCOPE, entity: LIST }] as const,
  personDetails: (ID: string) => [{ scope: PERSON_SCOPE, entity: DETAIL, Id: ID }] as const,
}

export async function postCreatePerson(person: PersonCreateDto) {
  return await apiClient.post('/Persons', person)
}

export function usePostCreatePerson(): [
  UseMutateAsyncFunction<AxiosResponse<PersonCreateDto>, Error, PersonCreateDto, unknown>,
  boolean,
  Error | null,
  boolean,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending, error, isSuccess, isError } = useMutation({
    mutationFn: postCreatePerson,
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allPerson() })
      return data
    },
    onError: (error) => {
      console.error('Error:', error)
    },
  })
  return [mutateAsync, isPending, error, isSuccess, isError]
}

export async function getAllPerson() {
  const url = '/Persons'
  const response = await apiClient.get<Person[]>(url)
  return response.data
}

export function useGetAllPerson(): [Person[], boolean] {
  const { isFetching, data } = useQuery({
    queryFn: getAllPerson,
    queryKey: keysFactory.allPerson(),
  })

  return [data ?? [], isFetching]
}

export async function getPersonById(Id: string) {
  const url = '/Persons/' + Id
  const response = await apiClient.get<Person>(url)
  return response.data
}

export function useGetPersonById(Id: string): [Person | null, boolean] {
  const { isFetching, data } = useQuery({
    queryFn: () => getPersonById(Id),
    queryKey: keysFactory.personDetails(Id),
  })

  return [data || null, isFetching]
}

const updatePersonById = async (person: Person) => {
  const url = '/Persons/' + person.id
  const response = await apiClient.put<Person>(url, person)
  return response.data
}

export function useUpdatePersonById(): [UseMutateAsyncFunction<Person, Error, Person, unknown>, boolean] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: updatePersonById,
    onSuccess: (response) => {
      queryClient.setQueryData(keysFactory.personDetails(response.id), response)
    },
  })
  return [mutateAsync, isPending]
}

async function deletePersonById(Id: string) {
  const url = '/Persons/' + Id
  const response = await apiClient.delete(url)
  return response
}

export default function useDeletePersonById(): [
  UseMutateAsyncFunction<AxiosResponse<void>, Error, string, unknown>,
  boolean,
] {
  const queryClient = useQueryClient()
  const { mutateAsync, isPending } = useMutation({
    mutationFn: deletePersonById,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: keysFactory.allPerson() })
    },
  })

  return [mutateAsync, isPending]
}
